// Live, persona-scoped notification feed. Polls the same endpoint (and at the
// same interval) as ExceptionQueueTable, so this needs no new backend route.
// Read/unread state is a per-user localStorage cutoff — this demo has no real
// email/notification backend (see docs/material-shortage-followup.md for the
// established mocked-integration convention), so persisting it server-side
// would be an unwarranted asymmetry.
//
// Wayne sees: new scans, exceptions, overs to review, solicitor inbox replies.
// Cary sees: escalations arriving from Wayne, CA tender-offer emails,
// advisor-support emails, and Proxy follow-ups that are due or overdue.
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppShell } from '../contexts/AppShellContext';
import { getMaterialReceipts } from '../services/materialReceiptService';
import { noteStamp } from '../data/receiptDraft';
import { WAYNE_INBOX_EMAILS } from '../data/wayneInboxEmails';
import { DEMO_EMAILS } from '../data/demoEmails';
import { ADVISOR_SUPPORT_EMAILS } from '../data/advisorSupportEmails';
import type { MaterialReceiptRow } from '../types/api';
import { useProxyData } from '../contexts/ProxyDataContext';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { useEmailInbox } from '../contexts/EmailInboxContext';
import { useTeamMessages, type TeamMessage } from '../contexts/TeamMessagesContext';
import { buildFollowUpTracker, isTrackerRowDue, OUTREACH_KIND_LABEL } from './proxyFollowUpTracker';

export type NotificationKind = 'escalation' | 'new-scan' | 'exception' | 'overs' | 'inbox' | 'followup-due' | 'team';

export interface EscalationNotification {
  id: string;
  kind: NotificationKind;
  receiptId?: string;
  scan?: string;
  jobId?: string;
  client: string;
  desc: string;
  timestamp: string;
  unread: boolean;
  // The heading (team messages) and the list row a click goes to.
  title?: string;
  href?: string;
}

const seenKey = (userId: string) => `opsai:notif_seen:${userId}`;
const readEmailsKey = (userId: string) => `opsai:notif_email_read:${userId}`;

const EXCEPTION_STATUSES = ['No Job Found', 'Received Short', 'On Hold'];
const NEW_SCAN_STATUSES = ['New', 'To Verify'];

// The receipt a team message is about: by scan when it has one, otherwise
// the job's receipt that's still open (Proxy's updates carry the job only).
function receiptFor(m: TeamMessage, rows: MaterialReceiptRow[]): MaterialReceiptRow | undefined {
  if (m.scan) {
    const byScan = rows.find(r => r.scan_number === m.scan);
    if (byScan) return byScan;
  }
  if (!m.jobId) return undefined;
  const forJob = rows.filter(r => r.job_numbers?.includes(m.jobId!));
  return forJob.find(r => ['Received Short', 'Follow Ups', 'To Verify'].includes(r.material_status)) ?? forJob[0];
}

// The list a receipt is on right now, with its row highlighted.
function receiptHref(r: MaterialReceiptRow): string {
  const page = r.material_status === 'Follow Ups' ? '/follow-ups'
    : r.escalated_to_team && EXCEPTION_STATUSES.includes(r.material_status) ? '/escalations'
      : EXCEPTION_STATUSES.includes(r.material_status) ? '/exceptions'
        : r.overs_status ? '/overs'
          : NEW_SCAN_STATUSES.includes(r.material_status) ? '/intake-queue'
            : '/receiving-log';
  return `${page}?highlight=${encodeURIComponent(r.id)}`;
}

// Where a team message leads: Proxy goes to the task Ace made from it;
// Fulfillment goes to the receipts it's about.
function teamMessageHref(m: TeamMessage, rows: MaterialReceiptRow[]): string {
  if (m.to === 'cary') {
    if (m.kind === 'received-short' && m.jobId) return `/proxy?taskId=shortage-${m.jobId}`;
    if (m.kind === 'material-merged' && m.jobId) return `/proxy?taskId=shortage-close-${m.jobId}`;
    if (m.kind === 'no-job-found' && m.scan) return `/proxy?taskId=unmatched-${m.scan}`;
  }
  const receipt = receiptFor(m, rows);
  return receipt ? receiptHref(receipt) : '/escalations';
}

export function useEscalationNotifications() {
  const { user } = useAuth();
  const { demoTeam } = useAppShell();
  const effectiveTeam = demoTeam ?? user?.team_id ?? null;
  const [rows, setRows] = useState<MaterialReceiptRow[]>([]);
  const [seenCutoff, setSeenCutoff] = useState<string | null>(null);
  const [readEmailIds, setReadEmailIds] = useState<Set<string>>(new Set());
  const { jobs: proxyJobs } = useProxyData();
  const { proxyEmails } = useEmailInbox();
  const { messages: teamMessages, isRead: teamMessageRead, markRead: markTeamMessagesRead } = useTeamMessages();
  // Keyed by job + chase + due date, so the next due date notifies again.
  const dueFollowUps = useMemo<EscalationNotification[]>(() => buildFollowUpTracker(proxyJobs, rows, PROXY_SEED_TODAY, proxyEmails)
    .filter(isTrackerRowDue)
    .map(r => {
      const id = `followup-due:${r.id}:${r.nextAction}`;
      return {
        id, kind: 'followup-due' as const, jobId: r.job.jobId, client: r.job.issuer,
        href: `/proxy/follow-ups?row=${encodeURIComponent(r.id)}`,
        desc: `${OUTREACH_KIND_LABEL[r.kind]} ${r.status === 'Due' ? 'due today' : 'overdue'}. Last sent ${r.daysElapsed}d ago to ${r.lastRecipient}`,
        timestamp: r.nextAction, unread: !readEmailIds.has(id),
      };
    }), [proxyJobs, rows, readEmailIds, proxyEmails]);

  useEffect(() => {
    if (!user) return;
    setSeenCutoff(localStorage.getItem(seenKey(user.id)));
    try {
      const raw = localStorage.getItem(readEmailsKey(user.id));
      setReadEmailIds(new Set(raw ? JSON.parse(raw) : []));
    } catch {
      setReadEmailIds(new Set());
    }
  }, [user]);

  useEffect(() => {
    if (!effectiveTeam) return;
    let alive = true;
    const load = () => getMaterialReceipts().then(r => { if (alive) setRows(r); }).catch(() => {});
    load();
    const iv = setInterval(load, 3000);
    return () => { alive = false; clearInterval(iv); };
  }, [effectiveTeam]);

  const notifications = useMemo<EscalationNotification[]>(() => {
    const out: EscalationNotification[] = [];

    // Hand-offs and updates between the teams, sent in Ace instead of by
    // email. Someone with no team (an admin) sees both sides, labelled.
    teamMessages.filter(m => !effectiveTeam || m.to === effectiveTeam).forEach(m => out.push({
      id: m.id, kind: 'team', desc: m.body, client: m.client ?? '', jobId: m.jobId, scan: m.scan,
      title: effectiveTeam ? m.title : `${m.to === 'cary' ? 'For Proxy' : 'For Fulfillment'}: ${m.title}`,
      timestamp: m.at, unread: !teamMessageRead(m.id), href: teamMessageHref(m, rows),
    }));
    if (!effectiveTeam) return out.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

    if (effectiveTeam === 'cary') {
      DEMO_EMAILS.forEach(e => out.push({
        id: e.id, kind: 'inbox', client: e.companyName, desc: e.subject, timestamp: '', href: `/email-intake?emailId=${e.id}`,
        unread: e.unread && !readEmailIds.has(e.id),
      }));
      ADVISOR_SUPPORT_EMAILS.filter(e => e.sla !== 'Done').forEach(e => out.push({
        id: e.id, kind: 'inbox', client: e.clientName, desc: e.subject, timestamp: '', href: `/advisor-support?emailId=${e.id}`,
        unread: !readEmailIds.has(e.id),
      }));
      out.push(...dueFollowUps);
    } else if (effectiveTeam === 'wayne') {
      rows
        .filter(r => NEW_SCAN_STATUSES.includes(r.material_status))
        .forEach(r => out.push({
          id: `${r.id}:new`, kind: 'new-scan', receiptId: r.id, scan: r.scan_number, href: receiptHref(r),
          client: r.client_names.join(', '), desc: 'New scan intake', timestamp: r.intake_date ?? '',
          unread: !seenCutoff || (r.intake_date ?? '') > seenCutoff,
        }));
      rows
        .filter(r => !r.escalated_to_team && EXCEPTION_STATUSES.includes(r.material_status))
        .forEach(r => out.push({
          id: `${r.id}:exc`, kind: 'exception', receiptId: r.id, scan: r.scan_number, href: receiptHref(r),
          client: r.client_names.join(', '), desc: r.material_status, timestamp: r.intake_date ?? '',
          unread: !seenCutoff || (r.intake_date ?? '') > seenCutoff,
        }));
      rows
        .filter(r => r.overs_status === 'Pending')
        .forEach(r => out.push({
          id: `${r.id}:overs`, kind: 'overs', receiptId: r.id, scan: r.scan_number, href: `/overs?highlight=${encodeURIComponent(r.id)}`,
          client: r.client_names.join(', '), desc: 'Overs need review', timestamp: r.intake_date ?? '',
          unread: !seenCutoff || (r.intake_date ?? '') > seenCutoff,
        }));
      WAYNE_INBOX_EMAILS.forEach(e => out.push({
        id: e.id, kind: 'inbox', client: e.client, desc: e.subject, timestamp: '', href: `/email-inbox/fulfillment?emailId=${e.id}`,
        unread: !readEmailIds.has(e.id) && e.unread,
      }));
    }

    return out.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [rows, effectiveTeam, seenCutoff, readEmailIds, dueFollowUps, teamMessages, teamMessageRead]);

  const unreadCount = useMemo(() => notifications.filter(n => n.unread).length, [notifications]);

  const markAllRead = () => {
    if (!user) return;
    const cutoff = noteStamp();
    localStorage.setItem(seenKey(user.id), cutoff);
    setSeenCutoff(cutoff);
    const allEmailIds = [...DEMO_EMAILS, ...ADVISOR_SUPPORT_EMAILS, ...WAYNE_INBOX_EMAILS, ...dueFollowUps].map(e => e.id);
    localStorage.setItem(readEmailsKey(user.id), JSON.stringify(allEmailIds));
    setReadEmailIds(new Set(allEmailIds));
    markTeamMessagesRead(teamMessages.filter(m => !effectiveTeam || m.to === effectiveTeam).map(m => m.id));
  };

  // Opening a team message marks it read.
  const markTeamMessageRead = (id: string) => markTeamMessagesRead([id]);
  // Opening an email or a due follow-up marks it read.
  const markItemRead = (id: string) => {
    if (!user) return;
    setReadEmailIds(prev => {
      const next = new Set(prev).add(id);
      localStorage.setItem(readEmailsKey(user.id), JSON.stringify([...next]));
      return next;
    });
  };

  return { notifications, unreadCount, markAllRead, markTeamMessageRead, markItemRead, effectiveTeam };
}
